import React, { useState } from 'react';
import { AthleteProfile, SportType } from '../types';
import { US_STATES, getCitiesForState } from '../data/usLocations';
import { ShareConfirmationNotification, ShareConfirmationData } from './notifications/ShareConfirmationNotification';
import { 
  Trophy, 
  Award, 
  Search, 
  Filter, 
  Share2, 
  Flame, 
  Users, 
  Sparkles, 
  ChevronRight,
  Swords,
  CheckCircle2,
  X,
  Crown,
  Zap,
  BarChart2,
  ArrowRightLeft,
  Check,
  TrendingUp,
  TrendingDown,
  Target
} from 'lucide-react';
import {
  ResponsiveContainer,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Legend,
  Tooltip
} from 'recharts';
import { getAthletePerformanceTrends } from '../utils/statTrends';
import { StatTrendIndicator } from './StatTrendIndicator';
import { RegionInspectorCard } from './regional/RegionInspectorCard';

interface LeaderboardViewProps {
  athletes: AthleteProfile[];
  currentUser: AthleteProfile;
  onShareAthlete: (athlete: AthleteProfile) => void;
  onSelectAthlete?: (athlete: AthleteProfile) => void;
  onUpdateUser?: (updated: Partial<AthleteProfile>) => void;
}

export const LeaderboardView: React.FC<LeaderboardViewProps> = ({
  athletes,
  currentUser,
  onShareAthlete,
  onSelectAthlete,
  onUpdateUser,
}) => {
  const [selectedSport, setSelectedSport] = useState<SportType | 'xp'>(currentUser.primarySport || 'volleyball');
  const [mainTab, setMainTab] = useState<'overall' | 'drills'>('overall');
  const [roleFilter, setRoleFilter] = useState<string>('all');
  const [proOnlyFilter, setProOnlyFilter] = useState<boolean>(false);
  const [friendsOnlyFilter, setFriendsOnlyFilter] = useState<boolean>(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [stateFilter, setStateFilter] = useState<string>('all');
  const [cityFilter, setCityFilter] = useState<string>('all');
  const [shareConfirmation, setShareConfirmation] = useState<ShareConfirmationData | null>(null);

  const handleShareAthlete = (athlete: AthleteProfile) => {
    onShareAthlete(athlete);
    setShareConfirmation({
      type: 'leaderboard_rank',
      title: `Rank Card Shared for ${athlete.name}`,
      subtitle: `Lvl ${athlete.level} ${athlete.primarySport?.toUpperCase() || ''} • ${athlete.xp} XP • Verified Leaderboard Card link copied!`,
      sport: athlete.primarySport,
      xpAwarded: 15,
    });
  };

  // Set of friend IDs including current user
  const friendIds = React.useMemo(() => {
    if (!currentUser?.friendsList) return new Set([currentUser?.id || '']);
    const ids = currentUser.friendsList.map((f) => (typeof f === 'string' ? f : f.id));
    return new Set([currentUser?.id || '', ...ids]);
  }, [currentUser?.friendsList, currentUser?.id]);

  // Selected athletes for comparison modal (up to 2 athlete IDs)
  const [selectedCompareIds, setSelectedCompareIds] = useState<string[]>([]);
  const [isCompareModalOpen, setIsCompareModalOpen] = useState(false);

  // Modal explicit selection override state
  const [modalAthleteAId, setModalAthleteAId] = useState<string>('');
  const [modalAthleteBId, setModalAthleteBId] = useState<string>('');

  // Sort athletes according to selected sport metric
  const sortedAthletes = [...athletes]
    .filter((a) => {
      const matchesRole = roleFilter === 'all' || a.role === roleFilter;
      const matchesPro = !proOnlyFilter || Boolean(a.isPro || a.isVerifiedPro || a.subscriptionTier === 'pro');
      const matchesFriends = !friendsOnlyFilter || friendIds.has(a.id);
      const matchesSearch =
        a.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        a.handle.toLowerCase().includes(searchQuery.toLowerCase()) ||
        a.schoolOrLeague.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (a.registeredCity && a.registeredCity.toLowerCase().includes(searchQuery.toLowerCase())) ||
        (a.registeredState && a.registeredState.toLowerCase().includes(searchQuery.toLowerCase()));

      let matchesState = true;
      if (stateFilter !== 'all') {
        const aState = (a.registeredState || a.location?.state || '').toLowerCase();
        const aLocCity = (a.location?.city || '').toLowerCase();
        const aSchool = (a.schoolOrLeague || '').toLowerCase();
        const sQuery = stateFilter.toLowerCase();
        matchesState = aState === sQuery || aLocCity.includes(sQuery) || aSchool.includes(sQuery);
      }

      let matchesCity = true;
      if (cityFilter !== 'all') {
        const aCity = (a.registeredCity || a.location?.city || '').toLowerCase();
        const aSchool = (a.schoolOrLeague || '').toLowerCase();
        const cQuery = cityFilter.toLowerCase();
        matchesCity = aCity.includes(cQuery) || aSchool.includes(cQuery);
      }

      return matchesRole && matchesPro && matchesFriends && matchesSearch && matchesState && matchesCity;
    })
    .sort((a, b) => {
      if (selectedSport === 'xp') {
        return b.xp - a.xp;
      }
      if (selectedSport === 'volleyball') {
        const killsA = a.stats.volleyball?.kills || 0;
        const killsB = b.stats.volleyball?.kills || 0;
        if (killsA !== killsB) return killsB - killsA;
        return b.xp - a.xp;
      }
      if (selectedSport === 'basketball') {
        const ppgA = a.stats.basketball.pts / (a.stats.basketball.gamesPlayed || 1);
        const ppgB = b.stats.basketball.pts / (b.stats.basketball.gamesPlayed || 1);
        return ppgB - ppgA;
      }
      if (selectedSport === 'baseball') {
        return b.stats.baseball.battingAvg - a.stats.baseball.battingAvg;
      }
      if (selectedSport === 'softball') {
        const baA = a.stats.softball?.battingAvg || 0;
        const baB = b.stats.softball?.battingAvg || 0;
        return baB - baA;
      }
      if (selectedSport === 'pickleball') {
        const wrA = a.stats.pickleball?.winRate || 0;
        const wrB = b.stats.pickleball?.winRate || 0;
        return wrB - wrA;
      }
      if (selectedSport === 'soccer') {
        return b.stats.soccer.goals - a.stats.soccer.goals;
      }
      return b.xp - a.xp;
    });

  // State for Local Legends availability filter and current user status
  const [localLegendsFilter, setLocalLegendsFilter] = useState<'all' | 'available'>('all');
  const [userStatus, setUserStatus] = useState<'Online' | 'In-Game' | 'Away'>(currentUser.userStatus || 'Online');

  const handleUpdateStatus = (newStatus: 'Online' | 'In-Game' | 'Away') => {
    setUserStatus(newStatus);
    if (onUpdateUser) {
      onUpdateUser({ userStatus: newStatus });
    }
  };

  // Derive Local Legends for user's registered city and state
  const myCity = (currentUser.registeredCity || 'New York').trim();
  const myState = (currentUser.registeredState || 'NY').trim();

  const getAthletePresence = (a: AthleteProfile) => {
    const status = a.id === currentUser.id ? userStatus : a.userStatus;
    if (status === 'In-Game') {
      return { label: 'In Game 🎮', isAvailable: true, dotBg: 'bg-amber-400 animate-pulse', badgeBg: 'bg-amber-400/20 text-amber-300 border-amber-400/30' };
    }
    if (status === 'Away') {
      return { label: 'Away 🟡', isAvailable: false, dotBg: 'bg-amber-500', badgeBg: 'bg-amber-500/20 text-amber-300 border-amber-500/30' };
    }
    if (status === 'Online') {
      return { label: 'Online 🟢', isAvailable: true, dotBg: 'bg-lime-400', badgeBg: 'bg-lime-400/20 text-lime-300 border-lime-400/30' };
    }

    const hash = (a.id || a.name).split('').reduce((acc, c) => acc + c.charCodeAt(0), 0);
    const states = [
      { label: 'Online 🟢', isAvailable: true, dotBg: 'bg-lime-400', badgeBg: 'bg-lime-400/20 text-lime-300 border-lime-400/30' },
      { label: 'In Game 🎮', isAvailable: true, dotBg: 'bg-amber-400 animate-pulse', badgeBg: 'bg-amber-400/20 text-amber-300 border-amber-400/30' },
      { label: 'Online 🟢', isAvailable: true, dotBg: 'bg-lime-400', badgeBg: 'bg-lime-400/20 text-lime-300 border-lime-400/30' },
      { label: 'Away 🟡', isAvailable: false, dotBg: 'bg-amber-500', badgeBg: 'bg-amber-500/20 text-amber-300 border-amber-500/30' },
      { label: 'Offline ⚪', isAvailable: false, dotBg: 'bg-slate-500', badgeBg: 'bg-slate-700/50 text-slate-300 border-slate-600/30' },
    ];
    return states[hash % states.length];
  };

  const localLegends = athletes
    .filter((a) => {
      const aCity = (a.registeredCity || a.location?.city || '').toLowerCase();
      const aState = (a.registeredState || a.location?.state || '').toLowerCase();
      const aSchool = (a.schoolOrLeague || '').toLowerCase();
      const isLocal = (
        aCity.includes(myCity.toLowerCase()) ||
        aState === myState.toLowerCase() ||
        aSchool.includes(myCity.toLowerCase())
      );
      if (!isLocal) return false;
      if (localLegendsFilter === 'available') {
        const presence = getAthletePresence(a);
        return presence.isAvailable;
      }
      return true;
    })
    .sort((a, b) => b.xp - a.xp)
    .slice(0, 8);

  // Toggle selection for comparison
  const toggleCompareAthlete = (athleteId: string) => {
    setSelectedCompareIds((prev) => {
      if (prev.includes(athleteId)) {
        return prev.filter((id) => id !== athleteId);
      }
      if (prev.length >= 2) {
        // Keep the latest selected and append new
        return [prev[1], athleteId];
      }
      return [...prev, athleteId];
    });
  };

  const handleOpenModal = () => {
    let aId = selectedCompareIds[0] || currentUser.id;
    let bId = selectedCompareIds[1] || (athletes.find((a) => a.id !== aId)?.id || currentUser.id);

    if (aId === bId && athletes.length > 1) {
      bId = athletes.find((a) => a.id !== aId)?.id || bId;
    }

    setModalAthleteAId(aId);
    setModalAthleteBId(bId);
    setIsCompareModalOpen(true);
  };

  // Get active comparison profiles
  const athleteA = athletes.find((a) => a.id === modalAthleteAId) || currentUser;
  const athleteB = athletes.find((a) => a.id === modalAthleteBId) || (athletes.find((a) => a.id !== athleteA.id) || currentUser);

  // Compute Radar Chart Metrics for Recharts
  const computeRadarData = (athA: AthleteProfile, athB: AthleteProfile) => {
    const ppgA = athA.stats.basketball.pts / (athA.stats.basketball.gamesPlayed || 1);
    const ppgB = athB.stats.basketball.pts / (athB.stats.basketball.gamesPlayed || 1);
    const maxPpg = Math.max(35, ppgA, ppgB);
    const scorePpgA = Math.min(100, Math.round((ppgA / maxPpg) * 100));
    const scorePpgB = Math.min(100, Math.round((ppgB / maxPpg) * 100));

    const gamesA = (athA.winCount + athA.lossCount) || 1;
    const gamesB = (athB.winCount + athB.lossCount) || 1;
    const winRateA = Math.round((athA.winCount / gamesA) * 100);
    const winRateB = Math.round((athB.winCount / gamesB) * 100);

    const levelScoreA = Math.min(100, Math.round((athA.level / 50) * 100));
    const levelScoreB = Math.min(100, Math.round((athB.level / 50) * 100));

    const apgA = athA.stats.basketball.ast / (athA.stats.basketball.gamesPlayed || 1);
    const apgB = athB.stats.basketball.ast / (athB.stats.basketball.gamesPlayed || 1);
    const maxApg = Math.max(12, apgA, apgB);
    const scoreApgA = Math.min(100, Math.round((apgA / maxApg) * 100));
    const scoreApgB = Math.min(100, Math.round((apgB / maxApg) * 100));

    const defA = (athA.stats.basketball.reb + athA.stats.basketball.stl + athA.stats.basketball.blk) / (athA.stats.basketball.gamesPlayed || 1);
    const defB = (athB.stats.basketball.reb + athB.stats.basketball.stl + athB.stats.basketball.blk) / (athB.stats.basketball.gamesPlayed || 1);
    const maxDef = Math.max(15, defA, defB);
    const scoreDefA = Math.min(100, Math.round((defA / maxDef) * 100));
    const scoreDefB = Math.min(100, Math.round((defB / maxDef) * 100));

    const fgA = athA.stats.basketball.fgAttempted > 0 
      ? Math.round((athA.stats.basketball.fgMade / athA.stats.basketball.fgAttempted) * 100) 
      : 50;
    const fgB = athB.stats.basketball.fgAttempted > 0 
      ? Math.round((athB.stats.basketball.fgMade / athB.stats.basketball.fgAttempted) * 100) 
      : 50;

    return [
      { subject: 'Scoring Power', [athA.name]: scorePpgA, [athB.name]: scorePpgB, fullMark: 100 },
      { subject: 'Win Rate %', [athA.name]: winRateA, [athB.name]: winRateB, fullMark: 100 },
      { subject: 'Level & XP', [athA.name]: levelScoreA, [athB.name]: levelScoreB, fullMark: 100 },
      { subject: 'Playmaking', [athA.name]: scoreApgA, [athB.name]: scoreApgB, fullMark: 100 },
      { subject: 'Defense & Rebs', [athA.name]: scoreDefA, [athB.name]: scoreDefB, fullMark: 100 },
      { subject: 'FG Shooting %', [athA.name]: fgA, [athB.name]: fgB, fullMark: 100 },
    ];
  };

  // Compute Comparative Bar Chart Metrics for Recharts
  const computeBarData = (athA: AthleteProfile, athB: AthleteProfile) => {
    const ppgA = Number((athA.stats.basketball.pts / (athA.stats.basketball.gamesPlayed || 1)).toFixed(1));
    const ppgB = Number((athB.stats.basketball.pts / (athB.stats.basketball.gamesPlayed || 1)).toFixed(1));

    const rpgA = Number((athA.stats.basketball.reb / (athA.stats.basketball.gamesPlayed || 1)).toFixed(1));
    const rpgB = Number((athB.stats.basketball.reb / (athB.stats.basketball.gamesPlayed || 1)).toFixed(1));

    const apgA = Number((athA.stats.basketball.ast / (athA.stats.basketball.gamesPlayed || 1)).toFixed(1));
    const apgB = Number((athB.stats.basketball.ast / (athB.stats.basketball.gamesPlayed || 1)).toFixed(1));

    const gamesA = (athA.winCount + athA.lossCount) || 1;
    const gamesB = (athB.winCount + athB.lossCount) || 1;
    const winRateA = Math.round((athA.winCount / gamesA) * 100);
    const winRateB = Math.round((athB.winCount / gamesB) * 100);

    const fgA = athA.stats.basketball.fgAttempted > 0 
      ? Math.round((athA.stats.basketball.fgMade / athA.stats.basketball.fgAttempted) * 100) 
      : 50;
    const fgB = athB.stats.basketball.fgAttempted > 0 
      ? Math.round((athB.stats.basketball.fgMade / athB.stats.basketball.fgAttempted) * 100) 
      : 50;

    return [
      { metric: 'PPG', [athA.name]: ppgA, [athB.name]: ppgB },
      { metric: 'RPG', [athA.name]: rpgA, [athB.name]: rpgB },
      { metric: 'APG', [athA.name]: apgA, [athB.name]: apgB },
      { metric: 'Win Rate %', [athA.name]: winRateA, [athB.name]: winRateB },
      { metric: 'FG %', [athA.name]: fgA, [athB.name]: fgB },
    ];
  };

  const radarData = computeRadarData(athleteA, athleteB);
  const barData = computeBarData(athleteA, athleteB);

  // Swap athlete positions
  const handleSwapAthletes = () => {
    const temp = modalAthleteAId;
    setModalAthleteAId(modalAthleteBId);
    setModalAthleteBId(temp);
  };

  return (
    <div className="space-y-6 pb-24 lg:pb-12 animate-fadeIn text-white relative">
      {/* Primary / Secondary Leaderboard Category Tabs */}
      <div className="flex items-center space-x-2 bg-indigo-950/80 p-2 rounded-2xl border border-white/10 font-mono">
        <button
          type="button"
          onClick={() => setMainTab('overall')}
          className={`px-5 py-2.5 rounded-xl text-xs font-black italic uppercase transition flex items-center space-x-2 ${
            mainTab === 'overall'
              ? 'bg-lime-400 text-black shadow-lg shadow-lime-400/20'
              : 'text-indigo-200 hover:text-white hover:bg-indigo-900/50'
          }`}
        >
          <Trophy className="w-4 h-4" />
          <span>🏆 Overall Sport Standings</span>
        </button>

        <button
          type="button"
          onClick={() => setMainTab('drills')}
          className={`px-5 py-2.5 rounded-xl text-xs font-black italic uppercase transition flex items-center space-x-2 ${
            mainTab === 'drills'
              ? 'bg-lime-400 text-black shadow-lg shadow-lime-400/20'
              : 'text-indigo-200 hover:text-white hover:bg-indigo-900/50'
          }`}
        >
          <Target className="w-4 h-4" />
          <span>🏐 Volleyball Drill Leaderboard</span>
        </button>
      </div>

      {/* Top Banner */}
      {mainTab === 'overall' ? (
        <>
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 bg-indigo-900/60 p-6 rounded-[2.5rem] border border-white/10 shadow-xl">
        <div>
          <div className="flex items-center space-x-2">
            <Trophy className="w-6 h-6 text-lime-400" />
            <h1 className="text-2xl font-black italic uppercase tracking-tight text-white">
              Athlete Leaderboard & Head-to-Head
            </h1>
          </div>
          <p className="text-xs text-indigo-200/70 mt-1 font-semibold">
            Top playground, high school, and college athletes ranked by scoring, efficiency, and level XP.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {/* Quick Head-to-Head Radar Compare Trigger */}
          <button
            onClick={handleOpenModal}
            className="px-4 py-2 bg-gradient-to-r from-lime-400 to-emerald-400 hover:from-lime-300 hover:to-emerald-300 text-black font-black italic uppercase text-xs rounded-xl shadow-lg shadow-lime-400/20 transition flex items-center space-x-2 shrink-0 border border-lime-300/40"
          >
            <Swords className="w-4 h-4" />
            <span>VS Compare Radar ({selectedCompareIds.length}/2)</span>
          </button>

          {/* Sport Metric Selector */}
          <div className="flex flex-wrap items-center bg-indigo-950 p-1.5 rounded-2xl border border-white/10">
            <button
              onClick={() => setSelectedSport('volleyball')}
              className={`px-3 py-1.5 rounded-xl text-xs font-black italic uppercase transition ${
                selectedSport === 'volleyball' ? 'bg-lime-400 text-black shadow-md' : 'text-indigo-200/60 hover:text-white'
              }`}
            >
              Volleyball Kills 🏐
            </button>
            <button
              onClick={() => setSelectedSport('basketball')}
              className={`px-3 py-1.5 rounded-xl text-xs font-black italic uppercase transition ${
                selectedSport === 'basketball' ? 'bg-lime-400 text-black shadow-md' : 'text-indigo-200/60 hover:text-white'
              }`}
            >
              B-Ball PPG 🏀
            </button>
            <button
              onClick={() => setSelectedSport('baseball')}
              className={`px-3 py-1.5 rounded-xl text-xs font-black italic uppercase transition ${
                selectedSport === 'baseball' ? 'bg-lime-400 text-black shadow-md' : 'text-indigo-200/60 hover:text-white'
              }`}
            >
              Baseball BA ⚾
            </button>
            <button
              onClick={() => setSelectedSport('softball')}
              className={`px-3 py-1.5 rounded-xl text-xs font-black italic uppercase transition ${
                selectedSport === 'softball' ? 'bg-lime-400 text-black shadow-md' : 'text-indigo-200/60 hover:text-white'
              }`}
            >
              Softball BA 🥎
            </button>
            <button
              onClick={() => setSelectedSport('pickleball')}
              className={`px-3 py-1.5 rounded-xl text-xs font-black italic uppercase transition ${
                selectedSport === 'pickleball' ? 'bg-lime-400 text-black shadow-md' : 'text-indigo-200/60 hover:text-white'
              }`}
            >
              Pickle Win% 🏓
            </button>
            <button
              onClick={() => setSelectedSport('soccer')}
              className={`px-3 py-1.5 rounded-xl text-xs font-black italic uppercase transition ${
                selectedSport === 'soccer' ? 'bg-lime-400 text-black shadow-md' : 'text-indigo-200/60 hover:text-white'
              }`}
            >
              Soccer Goals ⚽
            </button>
            <button
              onClick={() => setSelectedSport('xp')}
              className={`px-3 py-1.5 rounded-xl text-xs font-black italic uppercase transition ${
                selectedSport === 'xp' ? 'bg-lime-400 text-black shadow-md' : 'text-indigo-200/60 hover:text-white'
              }`}
            >
              Total XP ⚡
            </button>
          </div>
        </div>
      </div>

      {/* Search & Bracket Filters */}
      <div className="flex flex-col md:flex-row gap-3 bg-indigo-900/40 p-4 rounded-2xl border border-white/10">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3.5 top-3.5 text-indigo-300/60" />
          <input
            type="text"
            placeholder="Search athlete by name, handle, or school/league..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 bg-indigo-950 text-white text-sm font-medium rounded-xl border border-white/10 outline-none focus:ring-2 focus:ring-lime-400"
          />
        </div>

        <select
          value={roleFilter}
          onChange={(e) => setRoleFilter(e.target.value)}
          className="bg-indigo-950 text-white text-xs font-bold px-3 py-2 rounded-xl border border-white/10 outline-none focus:ring-2 focus:ring-lime-400"
        >
          <option value="all">All Brackets</option>
          <option value="high_school">High School Varsity</option>
          <option value="college">College / Intramural</option>
          <option value="playground_pro">Playground Legend</option>
        </select>

        {/* Pro Athlete Toggle Filter Button */}
        <button
          type="button"
          onClick={() => setProOnlyFilter(!proOnlyFilter)}
          className={`px-4 py-2 rounded-xl text-xs font-black italic uppercase transition flex items-center space-x-2 border shrink-0 ${
            proOnlyFilter
              ? 'bg-gradient-to-r from-amber-400 via-amber-300 to-yellow-400 text-black border-amber-300 shadow-lg shadow-amber-400/20 ring-2 ring-amber-400/50'
              : 'bg-indigo-950 text-indigo-300 border-white/10 hover:text-white hover:border-white/20'
          }`}
        >
          <Crown className={`w-4 h-4 ${proOnlyFilter ? 'fill-black text-black' : 'text-amber-400'}`} />
          <span>{proOnlyFilter ? 'Pro Athletes Only 👑' : 'Filter Pro Verified'}</span>
        </button>

        {/* Friends-Only Filter Toggle Button */}
        <button
          type="button"
          onClick={() => setFriendsOnlyFilter(!friendsOnlyFilter)}
          className={`px-4 py-2 rounded-xl text-xs font-black italic uppercase transition flex items-center space-x-2 border shrink-0 ${
            friendsOnlyFilter
              ? 'bg-lime-400 text-black border-lime-300 shadow-lg shadow-lime-400/20 ring-2 ring-lime-400/50'
              : 'bg-indigo-950 text-indigo-300 border-white/10 hover:text-white hover:border-white/20'
          }`}
          title="Filter leaderboard stats exclusively against your added friend connections"
        >
          <Users className={`w-4 h-4 ${friendsOnlyFilter ? 'text-black' : 'text-lime-400'}`} />
          <span>{friendsOnlyFilter ? 'Friends-Only League 👥' : 'Friends Only'}</span>
        </button>
      </div>

      {/* Regional Location Filter Bar (Local vs Statewide vs National) */}
      <div className="bg-indigo-950/80 p-3.5 rounded-2xl border border-white/10 flex flex-col md:flex-row md:items-center justify-between gap-3 shadow-lg">
        <div className="flex items-center space-x-2 overflow-x-auto pb-1 md:pb-0 text-xs">
          <span className="text-[10px] uppercase font-black tracking-widest text-lime-400 shrink-0">
            Regional View:
          </span>

          <button
            type="button"
            onClick={() => {
              if (currentUser.registeredCity) {
                setCityFilter(currentUser.registeredCity);
                setStateFilter('all');
              } else {
                setCityFilter('New York');
                setStateFilter('NY');
              }
            }}
            className={`px-3 py-1.5 rounded-xl font-black transition whitespace-nowrap flex items-center space-x-1 ${
              cityFilter !== 'all'
                ? 'bg-lime-400 text-black shadow-md shadow-lime-400/20'
                : 'bg-indigo-900/60 text-indigo-200 hover:text-white'
            }`}
          >
            <span>📍 Local ({currentUser.registeredCity || 'City'})</span>
          </button>

          <button
            type="button"
            onClick={() => {
              if (currentUser.registeredState) {
                setStateFilter(currentUser.registeredState);
                setCityFilter('all');
              } else {
                setStateFilter('NY');
                setCityFilter('all');
              }
            }}
            className={`px-3 py-1.5 rounded-xl font-black transition whitespace-nowrap flex items-center space-x-1 ${
              stateFilter !== 'all' && cityFilter === 'all'
                ? 'bg-lime-400 text-black shadow-md shadow-lime-400/20'
                : 'bg-indigo-900/60 text-indigo-200 hover:text-white'
            }`}
          >
            <span>🏛️ Statewide ({currentUser.registeredState || 'State'})</span>
          </button>

          <button
            type="button"
            onClick={() => { setStateFilter('all'); setCityFilter('all'); }}
            className={`px-3 py-1.5 rounded-xl font-black transition whitespace-nowrap flex items-center space-x-1 ${
              stateFilter === 'all' && cityFilter === 'all'
                ? 'bg-lime-400 text-black shadow-md shadow-lime-400/20'
                : 'bg-indigo-900/60 text-indigo-200 hover:text-white'
            }`}
          >
            <span>🇺🇸 National (All US)</span>
          </button>
        </div>

        <div className="flex flex-wrap items-center gap-2 text-xs">
          {/* Custom State Dropdown */}
          <select
            value={stateFilter}
            onChange={(e) => {
              setStateFilter(e.target.value);
              setCityFilter('all');
            }}
            className="bg-indigo-900/90 border border-white/10 rounded-xl px-2.5 py-1.5 text-xs font-bold text-lime-400 outline-none focus:ring-2 focus:ring-lime-400"
          >
            <option value="all">State (All States)</option>
            {US_STATES.map((st) => (
              <option key={st.code} value={st.code} className="bg-indigo-950 text-white">
                {st.name} ({st.code})
              </option>
            ))}
          </select>

          {/* Dynamic City Dropdown */}
          <select
            value={cityFilter}
            onChange={(e) => setCityFilter(e.target.value)}
            className="bg-indigo-900/90 border border-white/10 rounded-xl px-2.5 py-1.5 text-xs font-bold text-cyan-300 outline-none focus:ring-2 focus:ring-lime-400"
          >
            <option value="all">
              {stateFilter === 'all' ? 'City / Town (All Cities)' : `All Cities in ${stateFilter}`}
            </option>
            {(stateFilter !== 'all' ? getCitiesForState(stateFilter) : ['New York', 'Los Angeles', 'Chicago', 'Houston', 'Miami', 'San Francisco', 'Dallas', 'Seattle', 'Atlanta', 'Boston', 'Philadelphia', 'Phoenix']).map((city) => (
              <option key={city} value={city} className="bg-indigo-950 text-white">
                {city}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* LOCAL LEGENDS NEIGHBORHOOD SPOTLIGHT SECTION */}
      <div className="bg-gradient-to-r from-indigo-950 via-slate-900 to-indigo-950 p-5 rounded-[2.5rem] border-2 border-lime-400/40 shadow-2xl space-y-4 relative overflow-hidden">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-white/10 pb-3">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 rounded-2xl bg-lime-400 text-black flex items-center justify-center font-black text-xl shadow-lg shadow-lime-400/20">
              🌟
            </div>
            <div>
              <div className="flex items-center space-x-2">
                <span className="text-[10px] font-mono font-black uppercase tracking-widest text-lime-400">
                  NEIGHBORHOOD TALENT SPOTLIGHT
                </span>
                <span className="px-2 py-0.5 bg-lime-400/20 text-lime-300 text-[10px] font-bold rounded-full border border-lime-400/30">
                  {myCity}, {myState}
                </span>
              </div>
              <h2 className="text-xl font-black italic uppercase tracking-tight text-white flex items-center gap-2">
                <span>Local Legends 🏛️</span>
              </h2>
            </div>
          </div>
          
          <div className="flex flex-wrap items-center gap-2 self-start sm:self-auto">
            {/* Real-time Status Manual Toggle */}
            <div className="flex items-center bg-indigo-950/90 p-1 rounded-xl border border-lime-400/30 gap-1 text-[10px]">
              <span className="text-[9px] font-mono font-bold uppercase text-indigo-300 px-1">My Status:</span>
              <button
                type="button"
                onClick={() => handleUpdateStatus('Online')}
                className={`px-2 py-0.5 rounded-lg font-black uppercase transition flex items-center space-x-1 ${
                  userStatus === 'Online' ? 'bg-lime-400 text-black shadow' : 'text-indigo-300 hover:text-white'
                }`}
              >
                <span className="w-1.5 h-1.5 rounded-full bg-lime-400 inline-block" />
                <span>Online</span>
              </button>
              <button
                type="button"
                onClick={() => handleUpdateStatus('In-Game')}
                className={`px-2 py-0.5 rounded-lg font-black uppercase transition flex items-center space-x-1 ${
                  userStatus === 'In-Game' ? 'bg-amber-400 text-black shadow' : 'text-indigo-300 hover:text-white'
                }`}
              >
                <span className="w-1.5 h-1.5 rounded-full bg-amber-400 inline-block animate-pulse" />
                <span>In-Game</span>
              </button>
              <button
                type="button"
                onClick={() => handleUpdateStatus('Away')}
                className={`px-2 py-0.5 rounded-lg font-black uppercase transition flex items-center space-x-1 ${
                  userStatus === 'Away' ? 'bg-amber-500 text-black shadow' : 'text-indigo-300 hover:text-white'
                }`}
              >
                <span className="w-1.5 h-1.5 rounded-full bg-amber-500 inline-block" />
                <span>Away</span>
              </button>
            </div>

            {/* Availability Toggle Pill */}
            <div className="flex items-center bg-indigo-950 p-1 rounded-xl border border-white/10 gap-1 text-[10px]">
              <button
                type="button"
                onClick={() => setLocalLegendsFilter('all')}
                className={`px-3 py-1 rounded-lg font-black uppercase transition ${
                  localLegendsFilter === 'all' ? 'bg-lime-400 text-black shadow-md' : 'text-indigo-300 hover:text-white'
                }`}
              >
                All Athletes
              </button>
              <button
                type="button"
                onClick={() => setLocalLegendsFilter('available')}
                className={`px-3 py-1 rounded-lg font-black uppercase transition flex items-center space-x-1 ${
                  localLegendsFilter === 'available' ? 'bg-lime-400 text-black shadow-md' : 'text-indigo-300 hover:text-white'
                }`}
              >
                <span className="w-1.5 h-1.5 rounded-full bg-lime-400 inline-block animate-pulse" />
                <span>Available Now</span>
              </button>
            </div>

            <button
              type="button"
              onClick={() => {
                setCityFilter(myCity);
                setStateFilter(myState);
              }}
              className="px-3 py-1.5 bg-indigo-900 hover:bg-indigo-800 text-lime-300 font-bold text-xs rounded-xl border border-lime-400/30 transition flex items-center space-x-1.5"
            >
              <span>View Standings ({localLegends.length})</span>
              <ChevronRight className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>

        {/* Local Legends Grid Cards */}
        {localLegends.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {localLegends.map((athlete, idx) => {
              const isCompared = selectedCompareIds.includes(athlete.id);
              const rankTitles = ['Neighborhood MVP 👑', 'Park Royalty ⚡', 'Local Hero 🏆', 'Rising Prospect 🔥'];
              return (
                <div
                  key={athlete.id}
                  className="bg-indigo-900/60 p-3.5 rounded-2xl border border-white/10 hover:border-lime-400/50 transition flex flex-col justify-between space-y-3 relative group"
                >
                  <div className="flex items-center space-x-3">
                    <div className="relative shrink-0">
                      <img
                        src={athlete.avatar}
                        alt={athlete.name}
                        className="w-12 h-12 rounded-xl object-cover ring-2 ring-lime-400/80"
                        referrerPolicy="no-referrer"
                      />
                      {(() => {
                        const hash = (athlete.id || athlete.name).split('').reduce((acc, c) => acc + c.charCodeAt(0), 0);
                        const states = [
                          { label: 'Online 🟢', dotBg: 'bg-lime-400', badgeBg: 'bg-lime-400/20 text-lime-300 border-lime-400/30' },
                          { label: 'In Game 🎮', dotBg: 'bg-amber-400 animate-pulse', badgeBg: 'bg-amber-400/20 text-amber-300 border-amber-400/30' },
                          { label: 'Online 🟢', dotBg: 'bg-lime-400', badgeBg: 'bg-lime-400/20 text-lime-300 border-lime-400/30' },
                          { label: 'Busy 🔴', dotBg: 'bg-rose-500', badgeBg: 'bg-rose-500/20 text-rose-300 border-rose-500/30' },
                          { label: 'Offline ⚪', dotBg: 'bg-slate-500', badgeBg: 'bg-slate-700/50 text-slate-300 border-slate-600/30' },
                        ];
                        const st = states[hash % states.length];
                        return (
                          <span
                            className={`absolute -bottom-1 -right-1 w-3.5 h-3.5 rounded-full ${st.dotBg} border-2 border-indigo-950 shadow`}
                            title={`Status: ${st.label}`}
                          />
                        );
                      })()}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center justify-between gap-1">
                        <span className="text-[9px] font-black uppercase text-lime-400 block tracking-wider">
                          #{idx + 1} • {rankTitles[idx] || 'Local Legend'}
                        </span>
                        {(() => {
                          const hash = (athlete.id || athlete.name).split('').reduce((acc, c) => acc + c.charCodeAt(0), 0);
                          const states = [
                            { label: 'Online', badgeBg: 'bg-lime-400/20 text-lime-300 border-lime-400/30' },
                            { label: 'In Game 🎮', badgeBg: 'bg-amber-400/20 text-amber-300 border-amber-400/30' },
                            { label: 'Online', badgeBg: 'bg-lime-400/20 text-lime-300 border-lime-400/30' },
                            { label: 'Busy 🔴', badgeBg: 'bg-rose-500/20 text-rose-300 border-rose-500/30' },
                            { label: 'Offline ⚪', badgeBg: 'bg-slate-700/50 text-slate-300 border-slate-600/30' },
                          ];
                          const st = states[hash % states.length];
                          return (
                            <span className={`px-1.5 py-0.2 rounded text-[8px] font-mono font-black uppercase border ${st.badgeBg}`}>
                              {st.label}
                            </span>
                          );
                        })()}
                      </div>
                      <h4 className="font-extrabold text-sm text-white truncate flex items-center gap-1">
                        <span>{athlete.name}</span>
                        {athlete.isPro && <span className="text-[10px]" title="Pro Athlete">👑</span>}
                      </h4>
                      <p className="text-[10px] text-indigo-200/70 truncate font-semibold">
                        {athlete.schoolOrLeague || `${athlete.registeredCity || myCity}, ${athlete.registeredState || myState}`}
                      </p>
                    </div>
                  </div>

                  <div className="p-2 bg-indigo-950/80 rounded-xl border border-white/5 grid grid-cols-3 gap-1 text-center text-[10px]">
                    <div>
                      <span className="text-indigo-300/60 block text-[8px] uppercase font-bold">XP</span>
                      <span className="font-mono font-black text-lime-400">{athlete.xp}</span>
                    </div>
                    <div>
                      <span className="text-indigo-300/60 block text-[8px] uppercase font-bold">Sport</span>
                      <span className="font-bold text-white capitalize">{athlete.primarySport || 'Bball'}</span>
                    </div>
                    <div>
                      <span className="text-indigo-300/60 block text-[8px] uppercase font-bold">Level</span>
                      <span className="font-mono font-bold text-white">Lvl {athlete.level || 1}</span>
                    </div>
                  </div>

                  <div className="flex items-center space-x-1.5 pt-1">
                    <button
                      type="button"
                      onClick={() => toggleCompareAthlete(athlete.id)}
                      className={`flex-1 py-1.5 px-2 rounded-xl text-[10px] font-black uppercase transition flex items-center justify-center space-x-1 border ${
                        isCompared
                          ? 'bg-rose-500 text-white border-rose-400 shadow-md'
                          : 'bg-indigo-900 text-indigo-200 hover:text-white border-white/10'
                      }`}
                    >
                      <Swords className="w-3 h-3" />
                      <span>{isCompared ? 'Comparing' : 'Compare'}</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => (onSelectAthlete ? onSelectAthlete(athlete) : handleShareAthlete(athlete))}
                      className="py-1.5 px-2.5 bg-lime-400 hover:bg-lime-300 text-black font-black text-[10px] uppercase rounded-xl transition"
                    >
                      Profile
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="p-4 bg-indigo-950/50 rounded-2xl text-center text-xs text-indigo-200/70 font-semibold">
            No local registered legends in {myCity} yet. Be the first to claim top rank in your city! 🏆
          </div>
        )}
      </div>

      {/* Top 3 Podium Cards */}
      {sortedAthletes.length >= 3 && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {sortedAthletes.slice(0, 3).map((athlete, index) => {
            const ranks = [
              { label: '1st Place', badgeBg: 'bg-lime-400 text-black', ring: 'ring-lime-400', icon: '👑' },
              { label: '2nd Place', badgeBg: 'bg-rose-500 text-white', ring: 'ring-rose-500', icon: '🥈' },
              { label: '3rd Place', badgeBg: 'bg-indigo-700 text-white', ring: 'ring-indigo-400', icon: '🥉' },
            ];
            const badgeInfo = ranks[index];
            const isCompared = selectedCompareIds.includes(athlete.id);

            return (
              <div
                key={athlete.id}
                className="bg-indigo-900/60 p-6 rounded-[2.5rem] border border-white/10 shadow-xl relative flex flex-col items-center text-center space-y-3"
              >
                <span className={`absolute top-3 right-3 px-3 py-1 rounded-full text-[10px] font-black italic uppercase ${badgeInfo.badgeBg}`}>
                  {badgeInfo.icon} {badgeInfo.label}
                </span>

                <div className="relative mt-2">
                  <img
                    src={athlete.avatar}
                    alt={athlete.name}
                    className={`w-20 h-20 rounded-2xl object-cover ring-4 ${badgeInfo.ring}`}
                    referrerPolicy="no-referrer"
                  />
                  <span className="absolute -bottom-2 -right-2 bg-indigo-950 text-white font-black text-xs px-2 py-0.5 rounded-md border border-white/10">
                    #{athlete.jerseyNumber}
                  </span>
                </div>

                <div>
                  <h3 className="font-extrabold text-lg text-white flex items-center justify-center gap-1.5">
                    <span>{athlete.name}</span>
                    {athlete.isPro && (
                      <span className="bg-amber-400 text-black font-black text-[9px] px-1.5 py-0.5 rounded font-mono flex items-center gap-0.5 shrink-0" title="PRO Member">
                        <Crown className="w-3 h-3 fill-black" />
                        <span>PRO</span>
                      </span>
                    )}
                  </h3>
                  <p className="text-xs text-indigo-200/70 font-semibold">{athlete.schoolOrLeague}</p>
                </div>

                {(() => {
                  const trends = getAthletePerformanceTrends(athlete);
                  return (
                    <div className="w-full bg-indigo-950 p-3 rounded-2xl border border-white/10 flex items-center justify-around text-xs font-bold">
                      <div>
                        <span className="text-[10px] text-indigo-300/60 block font-black uppercase">Level</span>
                        <span className="text-lime-400 font-black italic">Lvl {athlete.level}</span>
                      </div>
                      <div className="border-r border-white/10 h-6" />
                      <div>
                        <span className="text-[10px] text-indigo-300/60 block font-black uppercase">PPG Avg</span>
                        <span className="text-white font-black italic">
                          {(athlete.stats.basketball.pts / (athlete.stats.basketball.gamesPlayed || 1)).toFixed(1)}
                        </span>
                      </div>
                      <div className="border-r border-white/10 h-6" />
                      <div>
                        <span className="text-[10px] text-indigo-300/60 block font-black uppercase">3G Trend</span>
                        <StatTrendIndicator trend={trends.pts} size="sm" />
                      </div>
                    </div>
                  );
                })()}

                <div className="grid grid-cols-2 gap-2 w-full pt-1">
                  <button
                    onClick={() => toggleCompareAthlete(athlete.id)}
                    className={`py-2 px-3 rounded-xl font-black italic text-xs uppercase flex items-center justify-center space-x-1.5 transition border ${
                      isCompared
                        ? 'bg-lime-400 text-black border-lime-300 shadow-md shadow-lime-400/20'
                        : 'bg-indigo-950 hover:bg-indigo-900 text-indigo-200 border-white/10'
                    }`}
                  >
                    {isCompared ? <Check className="w-3.5 h-3.5" /> : <Swords className="w-3.5 h-3.5" />}
                    <span>{isCompared ? 'Compared' : 'VS Select'}</span>
                  </button>

                  <button
                    onClick={() => handleShareAthlete(athlete)}
                    className="py-2 px-3 bg-indigo-950 hover:bg-indigo-900 text-indigo-200 font-black italic text-xs uppercase rounded-xl flex items-center justify-center space-x-1.5 transition border border-white/10"
                  >
                    <Share2 className="w-3.5 h-3.5" />
                    <span>Card</span>
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Full Leaderboard Table */}
      <div className="bg-indigo-900/60 rounded-[2.5rem] border border-white/10 shadow-xl overflow-hidden">
        <div className="px-6 py-4 border-b border-white/10 flex items-center justify-between">
          <h3 className="font-black italic uppercase text-white">
            Complete Rankings Table ({sortedAthletes.length} Athletes)
          </h3>
          <span className="text-xs text-indigo-200/60 font-semibold">
            Select any 2 athletes to launch side-by-side radar contrast
          </span>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm text-indigo-200/80">
            <thead className="bg-indigo-950/80 text-xs font-black text-indigo-300/60 uppercase tracking-wider border-b border-white/10">
              <tr>
                <th className="px-6 py-3">VS Compare</th>
                <th className="px-6 py-3">Rank</th>
                <th className="px-6 py-3">Athlete</th>
                <th className="px-6 py-3">Bracket / League</th>
                <th className="px-6 py-3">V-Ball Kills 🏐</th>
                <th className="px-6 py-3">B-Ball PPG</th>
                <th className="px-6 py-3">Softball BA</th>
                <th className="px-6 py-3">Pickle Win%</th>
                <th className="px-6 py-3">Record</th>
                <th className="px-6 py-3">Total XP</th>
                <th className="px-6 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {sortedAthletes.length === 0 ? (
                <tr>
                  <td colSpan={10} className="px-6 py-12 text-center">
                    <div className="space-y-4 max-w-sm mx-auto">
                      <div className="w-16 h-16 mx-auto rounded-3xl bg-indigo-950 border border-white/10 flex items-center justify-center text-lime-400 shadow-lg">
                        <Trophy className="w-8 h-8 text-lime-400" />
                      </div>
                      <div className="space-y-1">
                        <h4 className="text-base font-black italic uppercase text-white">No Athletes Found</h4>
                        <p className="text-xs text-indigo-200/70">
                          No leaderboard rankings match your athlete search query or active sport filter.
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          setSearchQuery('');
                          setSelectedSport('basketball');
                        }}
                        className="px-4 py-2 bg-lime-400 hover:bg-lime-300 text-black font-black italic uppercase text-xs rounded-xl transition inline-flex items-center space-x-1"
                      >
                        <X className="w-3.5 h-3.5" />
                        <span>Reset Search & Filters</span>
                      </button>
                    </div>
                  </td>
                </tr>
              ) : (
                sortedAthletes.map((athlete, idx) => {
                const isCurrent = athlete.id === currentUser.id;
                const isCompared = selectedCompareIds.includes(athlete.id);

                return (
                  <tr
                    key={athlete.id}
                    className={`hover:bg-indigo-900/40 transition ${
                      isCompared
                        ? 'bg-lime-400/15 font-semibold'
                        : isCurrent
                        ? 'bg-indigo-900/40 font-semibold'
                        : ''
                    }`}
                  >
                    <td className="px-6 py-4">
                      <button
                        onClick={() => toggleCompareAthlete(athlete.id)}
                        className={`p-2 rounded-xl transition flex items-center space-x-1 border ${
                          isCompared
                            ? 'bg-lime-400 text-black border-lime-300 font-black'
                            : 'bg-indigo-950 text-indigo-300 hover:text-white border-white/10'
                        }`}
                        title={isCompared ? 'Remove from Comparison' : 'Select for VS Comparison'}
                      >
                        {isCompared ? <Check className="w-4 h-4" /> : <Swords className="w-4 h-4" />}
                      </button>
                    </td>

                    <td className="px-6 py-4 font-black italic text-lime-400">
                      #{idx + 1}
                    </td>

                    <td className="px-6 py-4">
                      <div className="flex items-center space-x-3">
                        <img
                          src={athlete.avatar}
                          alt={athlete.name}
                          className="w-10 h-10 rounded-full object-cover ring-2 ring-lime-400/50"
                          referrerPolicy="no-referrer"
                        />
                        <div>
                          <div className="font-bold text-white flex items-center space-x-1">
                            <span>{athlete.name}</span>
                            {athlete.isPro && (
                              <span className="bg-amber-400 text-black font-black text-[9px] px-1.5 py-0.2 rounded font-mono flex items-center gap-0.5 shrink-0" title="PRO Member">
                                <Crown className="w-2.5 h-2.5 fill-black" />
                                <span>PRO</span>
                              </span>
                            )}
                            {isCurrent && (
                              <span className="text-[10px] bg-lime-400 text-black font-black px-1.5 py-0.2 rounded">YOU</span>
                            )}
                          </div>
                          <span className="text-xs text-indigo-300/60">{athlete.handle}</span>
                        </div>
                      </div>
                    </td>

                    <td className="px-6 py-4 text-xs">
                      <span className="font-semibold text-white block">
                        {athlete.schoolOrLeague}
                      </span>
                      <span className="text-indigo-300/60">{athlete.position}</span>
                    </td>

                    <td className="px-6 py-4 font-black italic text-cyan-300">
                      {athlete.stats.volleyball?.kills || 0} Kills
                    </td>

                    <td className="px-6 py-4 font-black italic text-white">
                      {(athlete.stats.basketball.pts / (athlete.stats.basketball.gamesPlayed || 1)).toFixed(1)}
                    </td>

                    <td className="px-6 py-4 font-black italic text-rose-300">
                      {athlete.stats.softball?.battingAvg ? athlete.stats.softball.battingAvg.toFixed(3).replace(/^0/, '') : '.420'}
                    </td>

                    <td className="px-6 py-4 font-black italic text-amber-300">
                      {athlete.stats.pickleball?.winRate ? `${Math.round(athlete.stats.pickleball.winRate * 100)}%` : '78%'}
                    </td>

                    <td className="px-6 py-4 text-xs font-black italic text-rose-400">
                      {athlete.winCount}W - {athlete.lossCount}L
                    </td>

                    <td className="px-6 py-4 font-black italic text-lime-400">
                      {athlete.xp} XP
                    </td>

                    <td className="px-6 py-4 text-right">
                      <button
                        onClick={() => handleShareAthlete(athlete)}
                        className="p-2 rounded-xl bg-indigo-950 hover:bg-indigo-900 text-indigo-200 transition border border-white/10"
                        title="Share Athlete Card"
                      >
                        <Share2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                );
              }))}
            </tbody>
          </table>
        </div>
      </div>
        </>
      ) : (
        /* VOLLEYBALL DRILL LEADERBOARD TAB */
        <div className="bg-indigo-900/60 p-6 rounded-[2.5rem] border border-white/10 shadow-xl space-y-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-white/10 pb-5">
            <div className="flex items-center space-x-3">
              <div className="p-3 bg-lime-400 text-black rounded-2xl shadow-lg">
                <Target className="w-6 h-6 stroke-[2.5]" />
              </div>
              <div>
                <span className="px-2 py-0.5 bg-lime-400/20 text-lime-300 font-mono font-black text-[10px] uppercase rounded border border-lime-400/30">
                  VOLLEYBALL DRILL STANDINGS
                </span>
                <h2 className="text-xl font-black italic uppercase text-white mt-1">
                  Top 10 Volleyball Drill Leaders
                </h2>
                <p className="text-xs text-indigo-200/80 mt-0.5">
                  Ranked specifically by successful Volleyball Skill Challenges & Drills completed.
                </p>
              </div>
            </div>

            <div className="flex items-center space-x-2 bg-indigo-950 px-4 py-2 rounded-2xl border border-white/10 text-xs font-mono">
              <span className="text-indigo-300">Metric:</span>
              <span className="text-lime-400 font-black">volleyballDrillCount</span>
            </div>
          </div>

          {/* Table of Top 10 Ranked Users */}
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="border-b border-white/10 text-[10px] font-black uppercase tracking-wider text-indigo-300/80 font-mono">
                  <th className="px-6 py-3">Rank</th>
                  <th className="px-6 py-3">Athlete Profile</th>
                  <th className="px-6 py-3">School / League</th>
                  <th className="px-6 py-3">Drills Completed 🏐</th>
                  <th className="px-6 py-3">Level & XP ⚡</th>
                  <th className="px-6 py-3 text-right">Share</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {[...athletes]
                  .sort((a, b) => (b.volleyballDrillCount || 0) - (a.volleyballDrillCount || 0))
                  .slice(0, 10)
                  .map((athlete, index) => {
                    const isCurrentUser = athlete.id === currentUser.id;
                    const rankMedals = ['👑 #1', '🥈 #2', '🥉 #3'];
                    const rankBadge = index < 3 ? rankMedals[index] : `#${index + 1}`;

                    return (
                      <tr
                        key={athlete.id}
                        className={`transition hover:bg-white/5 ${
                          isCurrentUser ? 'bg-lime-400/10 font-bold border-l-4 border-lime-400' : ''
                        }`}
                      >
                        <td className="px-6 py-4 font-black italic text-sm font-mono text-lime-400">
                          {rankBadge}
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center space-x-3">
                            <img
                              src={athlete.avatar}
                              alt={athlete.name}
                              className="w-10 h-10 rounded-xl object-cover ring-2 ring-lime-400/40"
                              referrerPolicy="no-referrer"
                            />
                            <div>
                              <div className="flex items-center space-x-1.5">
                                <span className="font-extrabold text-sm text-white">{athlete.name}</span>
                                {isCurrentUser && (
                                  <span className="px-1.5 py-0.5 bg-lime-400 text-black font-black text-[9px] uppercase rounded">
                                    YOU
                                  </span>
                                )}
                              </div>
                              <span className="text-xs text-indigo-300/60 font-mono block">{athlete.handle}</span>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-xs font-medium text-indigo-200">
                          {athlete.schoolOrLeague}
                        </td>
                        <td className="px-6 py-4 font-black italic text-lime-400 font-mono text-base">
                          {athlete.volleyballDrillCount || 0} Drills
                        </td>
                        <td className="px-6 py-4 text-xs font-mono font-bold text-cyan-300">
                          Lvl {athlete.level} ({athlete.xp} XP)
                        </td>
                        <td className="px-6 py-4 text-right">
                          <button
                            type="button"
                            onClick={() => handleShareAthlete(athlete)}
                            className="p-2 rounded-xl bg-indigo-950 hover:bg-indigo-900 text-indigo-200 transition border border-white/10"
                            title="Share Athlete Card"
                          >
                            <Share2 className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Floating Sticky VS Bar when 1 or 2 Athletes Selected */}
      {selectedCompareIds.length > 0 && (
        <div className="fixed bottom-20 lg:bottom-6 left-1/2 -translate-x-1/2 z-40 bg-indigo-950/95 backdrop-blur-md border-2 border-lime-400/80 p-3 px-6 rounded-full shadow-2xl shadow-black/80 flex items-center space-x-4 animate-bounce">
          <div className="flex items-center space-x-2">
            <Swords className="w-5 h-5 text-lime-400" />
            <span className="text-xs font-black italic uppercase text-white">
              VS Selected ({selectedCompareIds.length}/2):
            </span>
          </div>

          <div className="flex items-center -space-x-2">
            {selectedCompareIds.map((id) => {
              const ath = athletes.find((a) => a.id === id);
              if (!ath) return null;
              return (
                <img
                  key={id}
                  src={ath.avatar}
                  alt={ath.name}
                  className="w-8 h-8 rounded-full border-2 border-lime-400 object-cover"
                  referrerPolicy="no-referrer"
                />
              );
            })}
          </div>

          <button
            onClick={handleOpenModal}
            className="px-4 py-1.5 bg-lime-400 hover:bg-lime-300 text-black font-black italic uppercase text-xs rounded-full transition shadow-md shadow-lime-400/20"
          >
            Launch Radar Comparison 📊
          </button>

          <button
            onClick={() => setSelectedCompareIds([])}
            className="p-1 text-indigo-300 hover:text-white"
            title="Clear Selection"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* REGION INSPECTOR (At bottom of Leaderboard) */}
      <RegionInspectorCard 
        user={currentUser}
        registeredCity={currentUser.registeredCity || currentUser.location?.city}
        registeredState={currentUser.registeredState || currentUser.location?.state}
      />

      {/* Side-by-Side Comparison Modal with Recharts Radar */}
      {isCompareModalOpen && (
        <div className="fixed inset-0 z-50 bg-black/85 backdrop-blur-md flex items-center justify-center p-4 overflow-y-auto animate-fadeIn">
          <div className="bg-indigo-950 border-2 border-lime-400/50 w-full max-w-4xl rounded-[2.5rem] p-6 shadow-2xl space-y-6 max-h-[90vh] overflow-y-auto text-white">
            
            {/* Modal Header */}
            <div className="flex items-center justify-between border-b border-white/10 pb-4">
              <div className="flex items-center space-x-3">
                <div className="p-2.5 bg-lime-400 text-black rounded-2xl">
                  <BarChart2 className="w-6 h-6" />
                </div>
                <div>
                  <h2 className="text-xl font-black italic uppercase tracking-tight text-white flex items-center space-x-2">
                    <span>Head-to-Head Athlete Radar Comparison</span>
                  </h2>
                  <p className="text-xs text-indigo-200/70 font-semibold">
                    Visual contrast of scoring power, win rate, playmaking, defense, and XP progression
                  </p>
                </div>
              </div>

              <button
                onClick={() => setIsCompareModalOpen(false)}
                className="p-2 bg-indigo-900 hover:bg-indigo-800 text-indigo-200 rounded-xl transition border border-white/10"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Athlete Selector Controls Side-by-Side */}
            <div className="grid grid-cols-1 md:grid-cols-11 gap-3 items-center bg-indigo-900/60 p-4 rounded-2xl border border-white/10">
              <div className="md:col-span-5 space-y-1">
                <label className="text-[10px] font-black uppercase text-lime-400 block">
                  Athlete A (Lime Radar)
                </label>
                <select
                  value={modalAthleteAId}
                  onChange={(e) => setModalAthleteAId(e.target.value)}
                  className="w-full bg-indigo-950 text-white font-bold text-xs p-2.5 rounded-xl border border-lime-400/40 outline-none focus:ring-2 focus:ring-lime-400"
                >
                  {athletes.map((a) => (
                    <option key={`a-${a.id}`} value={a.id}>
                      {a.name} ({a.schoolOrLeague} - #{a.jerseyNumber})
                    </option>
                  ))}
                </select>
              </div>

              <div className="md:col-span-1 flex justify-center">
                <button
                  onClick={handleSwapAthletes}
                  className="p-2.5 bg-indigo-950 hover:bg-lime-400 hover:text-black text-lime-400 rounded-xl border border-white/10 transition shadow-md"
                  title="Swap Athlete Positions"
                >
                  <ArrowRightLeft className="w-4 h-4" />
                </button>
              </div>

              <div className="md:col-span-5 space-y-1">
                <label className="text-[10px] font-black uppercase text-rose-400 block">
                  Athlete B (Rose Radar)
                </label>
                <select
                  value={modalAthleteBId}
                  onChange={(e) => setModalAthleteBId(e.target.value)}
                  className="w-full bg-indigo-950 text-white font-bold text-xs p-2.5 rounded-xl border border-rose-400/40 outline-none focus:ring-2 focus:ring-rose-400"
                >
                  {athletes.map((a) => (
                    <option key={`b-${a.id}`} value={a.id}>
                      {a.name} ({a.schoolOrLeague} - #{a.jerseyNumber})
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Side-by-Side Profile Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Athlete A Profile Card */}
              {(() => {
                const trendsA = getAthletePerformanceTrends(athleteA);
                return (
                  <div className="bg-indigo-900/60 p-4 rounded-2xl border-2 border-lime-400/60 flex items-center space-x-4 shadow-lg relative overflow-hidden">
                    <div className="absolute top-0 right-0 bg-lime-400 text-black text-[9px] font-black uppercase px-2 py-0.5 rounded-bl-lg">
                      Athlete A
                    </div>
                    <img
                      src={athleteA.avatar}
                      alt={athleteA.name}
                      className="w-16 h-16 rounded-2xl object-cover ring-2 ring-lime-400 shrink-0"
                      referrerPolicy="no-referrer"
                    />
                    <div className="min-w-0 flex-1 space-y-1">
                      <h3 className="font-extrabold text-white text-base truncate flex items-center space-x-1.5">
                        <span>{athleteA.name}</span>
                        {athleteA.isPro && (
                          <span className="bg-amber-400 text-black font-black text-[9px] px-1.5 py-0.2 rounded font-mono flex items-center gap-0.5 shrink-0" title="PRO Member">
                            <Crown className="w-2.5 h-2.5 fill-black" />
                            <span>PRO</span>
                          </span>
                        )}
                        <span className="text-xs text-lime-400">#{athleteA.jerseyNumber}</span>
                      </h3>
                      <p className="text-xs text-indigo-200/70 truncate">{athleteA.schoolOrLeague}</p>
                      <div className="flex flex-wrap items-center gap-1.5 pt-0.5">
                        <span className="text-[10px] bg-lime-400/20 text-lime-300 font-bold px-2 py-0.5 rounded-md">
                          Lvl {athleteA.level} ({athleteA.xp} XP)
                        </span>
                        <StatTrendIndicator trend={trendsA.pts} size="sm" />
                      </div>
                    </div>
                  </div>
                );
              })()}

              {/* Athlete B Profile Card */}
              {(() => {
                const trendsB = getAthletePerformanceTrends(athleteB);
                return (
                  <div className="bg-indigo-900/60 p-4 rounded-2xl border-2 border-rose-500/60 flex items-center space-x-4 shadow-lg relative overflow-hidden">
                    <div className="absolute top-0 right-0 bg-rose-500 text-white text-[9px] font-black uppercase px-2 py-0.5 rounded-bl-lg">
                      Athlete B
                    </div>
                    <img
                      src={athleteB.avatar}
                      alt={athleteB.name}
                      className="w-16 h-16 rounded-2xl object-cover ring-2 ring-rose-500 shrink-0"
                      referrerPolicy="no-referrer"
                    />
                    <div className="min-w-0 flex-1 space-y-1">
                      <h3 className="font-extrabold text-white text-base truncate flex items-center space-x-1.5">
                        <span>{athleteB.name}</span>
                        {athleteB.isPro && (
                          <span className="bg-amber-400 text-black font-black text-[9px] px-1.5 py-0.2 rounded font-mono flex items-center gap-0.5 shrink-0" title="PRO Member">
                            <Crown className="w-2.5 h-2.5 fill-black" />
                            <span>PRO</span>
                          </span>
                        )}
                        <span className="text-xs text-rose-400">#{athleteB.jerseyNumber}</span>
                      </h3>
                      <p className="text-xs text-indigo-200/70 truncate">{athleteB.schoolOrLeague}</p>
                      <div className="flex flex-wrap items-center gap-1.5 pt-0.5">
                        <span className="text-[10px] bg-rose-500/20 text-rose-300 font-bold px-2 py-0.5 rounded-md">
                          Lvl {athleteB.level} ({athleteB.xp} XP)
                        </span>
                        <StatTrendIndicator trend={trendsB.pts} size="sm" />
                      </div>
                    </div>
                  </div>
                );
              })()}
            </div>

            {/* Side-by-Side Comparative Bar Chart Visualizer */}
            <div className="bg-indigo-900/80 p-5 rounded-3xl border border-white/10 shadow-xl space-y-2">
              <div className="flex items-center justify-between px-2">
                <span className="text-xs font-black italic uppercase text-lime-400 flex items-center space-x-1.5">
                  <BarChart2 className="w-4 h-4" />
                  <span>Side-by-Side Comparative Bar Chart</span>
                </span>
                <span className="text-[10px] text-indigo-300/60 uppercase font-semibold">
                  Direct Metric Comparison
                </span>
              </div>

              <div className="w-full h-72 pt-2">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={barData} margin={{ top: 15, right: 15, left: -15, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#312e81" />
                    <XAxis dataKey="metric" tick={{ fill: '#c7d2fe', fontSize: 11, fontWeight: 'bold' }} />
                    <YAxis tick={{ fill: '#94a3b8', fontSize: 10 }} />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: '#1e1b4b',
                        borderColor: '#4338ca',
                        borderRadius: '1rem',
                        color: '#fff',
                        fontSize: '12px',
                        fontWeight: 'bold',
                        boxShadow: '0 10px 25px -5px rgba(0,0,0,0.5)',
                      }}
                    />
                    <Legend wrapperStyle={{ color: '#ffffff', fontSize: '12px', fontWeight: 'bold', paddingTop: '10px' }} />
                    <Bar dataKey={athleteA.name} fill="#a3e635" radius={[6, 6, 0, 0]} name={`${athleteA.name} (Athlete A)`} />
                    <Bar dataKey={athleteB.name} fill="#f43f5e" radius={[6, 6, 0, 0]} name={`${athleteB.name} (Athlete B)`} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Recharts Radar Chart Visualizer */}
            <div className="bg-indigo-900/80 p-5 rounded-3xl border border-white/10 shadow-xl space-y-2">
              <div className="flex items-center justify-between px-2">
                <span className="text-xs font-black italic uppercase text-lime-400 flex items-center space-x-1">
                  <Sparkles className="w-3.5 h-3.5" />
                  <span>Performance Radar Overlay</span>
                </span>
                <span className="text-[10px] text-indigo-300/60 uppercase font-semibold">
                  Normalized 0-100 Performance Scale
                </span>
              </div>

              <div className="w-full h-80 flex items-center justify-center pt-2">
                <ResponsiveContainer width="100%" height="100%">
                  <RadarChart outerRadius="70%" data={radarData}>
                    <PolarGrid stroke="#312e81" />
                    <PolarAngleAxis
                      dataKey="subject"
                      tick={{ fill: '#c7d2fe', fontSize: 11, fontWeight: 'bold' }}
                    />
                    <PolarRadiusAxis angle={30} domain={[0, 100]} stroke="#4338ca" tick={false} />
                    <Radar
                      name={athleteA.name}
                      dataKey={athleteA.name}
                      stroke="#a3e635"
                      fill="#a3e635"
                      fillOpacity={0.45}
                    />
                    <Radar
                      name={athleteB.name}
                      dataKey={athleteB.name}
                      stroke="#f43f5e"
                      fill="#f43f5e"
                      fillOpacity={0.45}
                    />
                    <Legend wrapperStyle={{ color: '#ffffff', fontSize: '12px', fontWeight: 'bold', paddingTop: '10px' }} />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: '#1e1b4b',
                        borderColor: '#4338ca',
                        borderRadius: '1rem',
                        color: '#fff',
                        fontSize: '12px',
                        fontWeight: 'bold',
                        boxShadow: '0 10px 25px -5px rgba(0,0,0,0.5)'
                      }}
                    />
                  </RadarChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Side-by-Side Category Breakdown Table */}
            <div className="bg-indigo-900/40 rounded-2xl border border-white/10 overflow-hidden space-y-1">
              <div className="px-4 py-3 bg-indigo-950 border-b border-white/10 flex items-center justify-between">
                <span className="text-xs font-black italic uppercase text-white">
                  Head-to-Head Detailed Stat Matrix
                </span>
                <span className="text-[10px] text-indigo-300/60 italic font-semibold">
                  👑 Crown indicates leader in category
                </span>
              </div>

              <div className="divide-y divide-white/5 text-xs">
                {/* Basketball PPG */}
                {(() => {
                  const ppgA = (athleteA.stats.basketball.pts / (athleteA.stats.basketball.gamesPlayed || 1)).toFixed(1);
                  const ppgB = (athleteB.stats.basketball.pts / (athleteB.stats.basketball.gamesPlayed || 1)).toFixed(1);
                  const numA = parseFloat(ppgA);
                  const numB = parseFloat(ppgB);
                  return (
                    <div className="px-4 py-2.5 grid grid-cols-3 items-center text-center">
                      <div className={`font-black italic ${numA >= numB ? 'text-lime-400' : 'text-indigo-200/70'}`}>
                        {ppgA} PPG {numA > numB && '👑'}
                      </div>
                      <div className="text-[10px] font-black uppercase text-indigo-300/60">
                        Basketball PPG
                      </div>
                      <div className={`font-black italic ${numB >= numA ? 'text-rose-400' : 'text-indigo-200/70'}`}>
                        {numB > numA && '👑 '} {ppgB} PPG
                      </div>
                    </div>
                  );
                })()}

                {/* Win Rate */}
                {(() => {
                  const wrA = (athleteA.winCount + athleteA.lossCount) > 0 ? Math.round((athleteA.winCount / (athleteA.winCount + athleteA.lossCount)) * 100) : 0;
                  const wrB = (athleteB.winCount + athleteB.lossCount) > 0 ? Math.round((athleteB.winCount / (athleteB.winCount + athleteB.lossCount)) * 100) : 0;
                  return (
                    <div className="px-4 py-2.5 grid grid-cols-3 items-center text-center bg-indigo-950/30">
                      <div className={`font-black italic ${wrA >= wrB ? 'text-lime-400' : 'text-indigo-200/70'}`}>
                        {wrA}% {wrA > wrB && '👑'}
                      </div>
                      <div className="text-[10px] font-black uppercase text-indigo-300/60">
                        Overall Win Rate
                      </div>
                      <div className={`font-black italic ${wrB >= wrA ? 'text-rose-400' : 'text-indigo-200/70'}`}>
                        {wrB > wrA && '👑 '} {wrB}%
                      </div>
                    </div>
                  );
                })()}

                {/* Level & XP */}
                <div className="px-4 py-2.5 grid grid-cols-3 items-center text-center">
                  <div className={`font-black italic ${athleteA.xp >= athleteB.xp ? 'text-lime-400' : 'text-indigo-200/70'}`}>
                    Lvl {athleteA.level} ({athleteA.xp} XP) {athleteA.xp > athleteB.xp && '👑'}
                  </div>
                  <div className="text-[10px] font-black uppercase text-indigo-300/60">
                    Level & Experience
                  </div>
                  <div className={`font-black italic ${athleteB.xp >= athleteA.xp ? 'text-rose-400' : 'text-indigo-200/70'}`}>
                    {athleteB.xp > athleteA.xp && '👑 '} Lvl {athleteB.level} ({athleteB.xp} XP)
                  </div>
                </div>

                {/* Softball / Baseball BA */}
                {(() => {
                  const baA = athleteA.stats.softball?.battingAvg || athleteA.stats.baseball?.battingAvg || 0.400;
                  const baB = athleteB.stats.softball?.battingAvg || athleteB.stats.baseball?.battingAvg || 0.400;
                  return (
                    <div className="px-4 py-2.5 grid grid-cols-3 items-center text-center bg-indigo-950/30">
                      <div className={`font-black italic ${baA >= baB ? 'text-lime-400' : 'text-indigo-200/70'}`}>
                        .{Math.round(baA * 1000)} {baA > baB && '👑'}
                      </div>
                      <div className="text-[10px] font-black uppercase text-indigo-300/60">
                        Batting Average
                      </div>
                      <div className={`font-black italic ${baB >= baA ? 'text-rose-400' : 'text-indigo-200/70'}`}>
                        {baB > baA && '👑 '} .{Math.round(baB * 1000)}
                      </div>
                    </div>
                  );
                })()}

                {/* Pickleball Win % */}
                {(() => {
                  const pkA = athleteA.stats.pickleball?.winRate || 0.75;
                  const pkB = athleteB.stats.pickleball?.winRate || 0.75;
                  return (
                    <div className="px-4 py-2.5 grid grid-cols-3 items-center text-center">
                      <div className={`font-black italic ${pkA >= pkB ? 'text-lime-400' : 'text-indigo-200/70'}`}>
                        {Math.round(pkA * 100)}% {pkA > pkB && '👑'}
                      </div>
                      <div className="text-[10px] font-black uppercase text-indigo-300/60">
                        Pickleball Win %
                      </div>
                      <div className={`font-black italic ${pkB >= pkA ? 'text-rose-400' : 'text-indigo-200/70'}`}>
                        {pkB > pkA && '👑 '} {Math.round(pkB * 100)}%
                      </div>
                    </div>
                  );
                })()}
              </div>
            </div>

            {/* Modal Actions */}
            <div className="flex items-center justify-between pt-2 border-t border-white/10">
              <button
                onClick={() => {
                  handleShareAthlete(athleteA);
                }}
                className="px-4 py-2.5 bg-indigo-900 hover:bg-indigo-800 text-indigo-200 font-black italic uppercase text-xs rounded-xl transition border border-white/10 flex items-center space-x-1.5"
              >
                <Share2 className="w-4 h-4 text-lime-400" />
                <span>Share Comparison Card</span>
              </button>

              <button
                onClick={() => setIsCompareModalOpen(false)}
                className="px-6 py-2.5 bg-lime-400 hover:bg-lime-300 text-black font-black italic uppercase text-xs rounded-xl shadow-lg shadow-lime-400/20 transition"
              >
                Done / Close
              </button>
            </div>

          </div>
        </div>
      )}

      {/* Share Confirmation Feedback Notification Overlay */}
      <ShareConfirmationNotification
        data={shareConfirmation}
        onClose={() => setShareConfirmation(null)}
      />
    </div>
  );
};
